import * as React from 'react';
import {
  COUNTRIES_SELECT_OPTIONS_USA_FIRST,
  DARK_PLACEHOLDER,
  getNewValue,
  OnboardingSections,
  ROUTE_PATH,
} from '../../../constants';
import { OnboardingSection } from '../OnboardingSection';
import { Accordion, Button } from '@rtkwlf/ui';
import InputDropdown from '../../../widgets/InputDropdown';
import { ValueType } from 'react-select/src/types';
import {
  AccordionFooterPropType,
  OBAWSRegions,
  OBCloudSensorApplication,
  OBCloudSensors,
  OBCloudSensorTypes,
  OBGCPLicenses,
  OBIManageLicenses,
  OBO365Licenses,
  OBSFLicenses,
  OptionType,
} from '../../../types';
import { Input, Label } from 'reactstrap';
import { useDispatch, useSelector } from 'react-redux';
import {
  DEFAULT_CLOUD_SENSORS,
  onboardingSelector,
} from '../../../reducers/onboarding';
import { ONBOARDING_ACTIONS } from '../../../actions/onboarding';
import { apiRequest } from '../../../APIClient';
import { useOBAccordionChange } from '../../../hooks';
import { OnboardingAccordionHeader } from '../OnboardingAccordionHeader';
import { getAccordionHeader } from '../../../utils';
import { sessionSelector } from '../../../reducers/session';
import { Link } from 'react-router-dom';

const ACCORDION_DESCRIPTION = (
  <span>
    This information helps us to monitor your cloud applications. Go to{' '}
    <Link to={ROUTE_PATH.SELF_SERVICE.CONNECTED_ACCOUNTS.MAIN}>
      Connected Accounts
    </Link>{' '}
    to begin adding cloud accounts.
  </span>
);

export enum ACCORDION_KEYS {
  GEO = 'Approved Login Countries',
  AWS = 'Amazon Web Services (AWS)',
  BOX = 'Box',
  GSUITE = ' GSuite',
  IMANAGE = 'iManage Threat Manager',
  AZURE = 'Microsoft Azure',
  O365 = 'Office 365',
  SF = 'Salesforce',
  GCP = 'Google Cloud Platform',
}

const ACCORDION_ITEMS: `${ACCORDION_KEYS}`[] = [
  ACCORDION_KEYS.GEO,
  ACCORDION_KEYS.AWS,
  ACCORDION_KEYS.BOX,
  ACCORDION_KEYS.GSUITE,
  ACCORDION_KEYS.AZURE,
  ACCORDION_KEYS.O365,
  ACCORDION_KEYS.SF,
  ACCORDION_KEYS.IMANAGE,
];

// Customer friendly labels
export const CloudSensorLabels: Record<OBCloudSensorTypes, string> = {
  aws: 'AWS',
  azure: 'Azure',
  o365: 'M365',
  salesforce: 'Salesforce',
  box: 'Box',
  gsuite: 'GWorkspace',
  gcp: 'Google Cloud Platform',
  imanage: 'iManage Threat Manager',
};

const REGIONS_MAP: Record<OBAWSRegions, string> = {
  'us-east-1': 'US East (N. Virginia)',
  'us-east-2': 'US East (Ohio)',
  'us-west-1': 'US West (N. California)',
  'us-west-2': 'US West (Oregon)',
  'ca-central-1': 'Canada (Central)',
  'sa-east-1': 'South America (São Paulo)',
  'eu-west-1': 'EU (Ireland)',
  'eu-west-2': 'EU (London)',
  'eu-west-3': 'EU (Paris)',
  'eu-central-1': 'EU (Frankfurt)',
  'eu-north-1': 'EU (Stockholm)',
  'ap-south-1': 'Asia Pacific (Mumbai)',
  'ap-northeast-2': 'Asia Pacific (Seoul)',
  'ap-southeast-1': 'Asia Pacific (Singapore)',
  'ap-southeast-2': 'Asia Pacific (Sydney)',
  'ap-northeast-1': 'Asia Pacific (Tokyo)',
  'af-south-1': 'Africa (Cape Town)',
};

const REGIONS: OptionType[] = Object.entries(REGIONS_MAP).map(
  ([value, label]) => ({
    value,
    label,
  }),
);

const SF_LICENSES: OptionType[] = Object.values(OBSFLicenses).map((value) => ({
  value: value,
  label: value,
}));

const IMANAGE_LICENSES: OptionType[] = Object.values(OBIManageLicenses).map(
  (value) => ({
    value: value,
    label: value,
  }),
);

const O365_LICENSES: OptionType[] = Object.values(OBO365Licenses).map(
  (value) => ({ value: value, label: value }),
);

const GCP_LICENSES: OptionType[] = Object.values(OBGCPLicenses).map(
  (value) => ({ value: value, label: value }),
);

const countryLabels: { [key: string]: string } = {};
for (let c of COUNTRIES_SELECT_OPTIONS_USA_FIRST) {
  countryLabels[c.value] = c.label;
}

const SensorItemFooter = ({
  canEdit,
  isComplete,
  disabled,
  sensorType,
}: AccordionFooterPropType & { sensorType: OBCloudSensorApplication }) => {
  const dispatch = useDispatch();

  return (
    <div className="ob-mark-complete-button mt-2">
      <Button
        type="primary"
        data-cy={`${sensorType}CompleteButton`}
        ghost={isComplete}
        onClick={() => {
          dispatch(ONBOARDING_ACTIONS.TOGGLE_CLOUD_SENSOR_COMPLETE(sensorType));
          dispatch(apiRequest.saveOBCloudSensors());
        }}
        disabled={!canEdit || disabled}
      >
        {isComplete ? 'Mark Incomplete' : 'Mark Complete'}
      </Button>
    </div>
  );
};

const OnboardingCloudSensorConfig = () => {
  const dispatch = useDispatch();
  const cloudSensors = useSelector(onboardingSelector.getCloudSensors);
  const canEdit = useSelector(sessionSelector.canEditOnboarding);
  const awsCompleted = cloudSensors.aws.completed;
  const azureCompleted = cloudSensors.azure.completed;
  const o365Completed = cloudSensors.o365.completed;
  const sfCompleted = cloudSensors.sf.completed;
  const geoCompleted = cloudSensors.geo.completed;
  const iManageCompleted = cloudSensors.imanage.completed;
  const purchasedCloudSensors = useSelector(
    onboardingSelector.getOnboardingProducts,
  ).cloudSensors;

  React.useEffect(() => {
    if (cloudSensors === DEFAULT_CLOUD_SENSORS) {
      dispatch(apiRequest.getOBCloudSensors());
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dispatch]);

  const accordionItems = React.useMemo(() => {
    if (purchasedCloudSensors.includes(OBCloudSensorTypes.gcp)) {
      return [...ACCORDION_ITEMS, ACCORDION_KEYS.GCP];
    } else {
      return ACCORDION_ITEMS;
    }
  }, [purchasedCloudSensors]);

  const { collapseAll, isCollapsed, expandAll, expandedItems, handleOnChange } =
    useOBAccordionChange(accordionItems);

  const [awsRegions, setAWSRegions] = React.useState<OptionType[]>(
    cloudSensors.aws.regions?.map((region) => ({
      value: region,
      label: REGIONS_MAP[region as OBAWSRegions],
    })) ?? [],
  );
  // Remove this when cloudWatch is removed entirely
  const [, setAWSCloudWatch] = React.useState(cloudSensors.aws.cloudWatch);
  const [awsCloudTrail, setAWSCloudTrail] = React.useState(
    cloudSensors.aws.cloudTrail,
  );
  const [awsGuardDuty, setAWSGuardDuty] = React.useState(
    cloudSensors.aws.guardDuty,
  );
  const [awsAcl, setAWSAcl] = React.useState(cloudSensors.aws.acl);
  const [awsEks, setAWSEks] = React.useState(cloudSensors.aws.eks);
  const [azureSecurityCenter, setAzureSecurityCenter] = React.useState(
    cloudSensors.azure.securityCenter,
  );
  const [azureSentinel, setAzureSentinel] = React.useState(
    cloudSensors.azure.sentinel,
  );
  const [o365Licenses, setO365Licenses] = React.useState<OptionType[]>(
    cloudSensors.o365.licenses?.map((license) => ({
      value: license,
      label: license,
    })) ?? [],
  );
  const [o365DLP, setO365DLP] = React.useState(cloudSensors.o365.dlp);
  const [sfLicenses, setSFLicenses] = React.useState<OptionType[]>(
    cloudSensors.sf.licenses?.map((license) => ({
      value: license,
      label: license,
    })) ?? [],
  );
  const [iManageLicenses, setIManageLicenses] = React.useState<OptionType[]>(
    cloudSensors.imanage.licenses?.map((license) => ({
      value: license,
      label: license,
    })) ?? [],
  );
  const [gcpLicenses, setGCPLicenses] = React.useState<OptionType[]>(
    cloudSensors.gcp.licenses?.map((license) => ({
      value: license,
      label: license,
    })) ?? [],
  );

  const [geoCountries, setGeoCountries] = React.useState<OptionType[]>(
    cloudSensors.geo.countries?.map((country) => ({
      value: country,
      label: countryLabels[country],
    })) ?? [],
  );

  const [isUpdated, setIsUpdated] = React.useState(false);

  React.useEffect(() => {
    if (!isUpdated && cloudSensors !== DEFAULT_CLOUD_SENSORS) {
      setAWSRegions(
        cloudSensors.aws.regions?.map((region) => ({
          value: region,
          label: REGIONS_MAP[region as keyof typeof REGIONS_MAP],
        })) ?? [],
      );
      // Since cloudWatch and cloudTrail are both supported, set initial state of cloudTrail based on older cloudWatch value
      // Change this to be cloudSensors.aws.cloudTrail when cloudWatch is removed entirely
      setAWSCloudTrail(cloudSensors.aws.cloudWatch);
      // Remove this when cloudWatch is removed entirely
      setAWSCloudWatch(cloudSensors.aws.cloudWatch);
      setAWSGuardDuty(cloudSensors.aws.guardDuty);
      setAWSAcl(cloudSensors.aws.acl);
      setAWSEks(cloudSensors.aws.eks);
      setO365Licenses(
        cloudSensors.o365.licenses?.map((license) => ({
          value: license,
          label: license,
        })) ?? [],
      );
      setO365DLP(cloudSensors.o365.dlp);
      setAzureSentinel(cloudSensors.azure.sentinel);
      setAzureSecurityCenter(cloudSensors.azure.securityCenter);
      setSFLicenses(
        cloudSensors.sf.licenses?.map((license) => ({
          value: license,
          label: license,
        })) ?? [],
      );
      setGCPLicenses(
        cloudSensors.gcp.licenses?.map((license) => ({
          value: license,
          label: license,
        })) ?? [],
      );
      setGeoCountries(
        cloudSensors.geo.countries?.map((country) => ({
          value: country,
          label: countryLabels[country],
        })) ?? [],
      );
      setIsUpdated(true);
    }
  }, [
    isUpdated,
    cloudSensors,
    setAWSRegions,
    setAWSCloudWatch,
    setAWSCloudTrail,
    setAWSGuardDuty,
    setAWSAcl,
    setAWSEks,
    setO365Licenses,
    setO365DLP,
    setAzureSentinel,
    setAzureSecurityCenter,
    setSFLicenses,
    setGeoCountries,
    setGCPLicenses,
  ]);

  const handleSelectChange = (
    value: ValueType<OptionType>,
    callback: (list: OptionType[]) => void,
    model: {
      cloudSensor: keyof Omit<OBCloudSensors, 'completed'>;
      field: string;
    },
  ) => {
    const newValue = getNewValue(value, true) || [];
    callback(newValue);
    dispatch(
      ONBOARDING_ACTIONS.EDIT_CLOUD_SENSORS({
        ...cloudSensors,
        [model.cloudSensor]: {
          ...cloudSensors[model.cloudSensor],
          [model.field]: newValue.map((region) => region.value),
        },
      }),
    );

    dispatch(apiRequest.saveOBCloudSensors());
  };

  const handleCheckboxChange = (
    e: React.SyntheticEvent<HTMLInputElement>,
    callback: (checked: boolean) => void,
    model: {
      cloudSensor: keyof Omit<OBCloudSensors, 'completed'>;
      field: string;
    },
  ) => {
    callback(e.currentTarget.checked);
    dispatch(
      ONBOARDING_ACTIONS.EDIT_CLOUD_SENSORS({
        ...cloudSensors,
        [model.cloudSensor]: {
          ...cloudSensors[model.cloudSensor],
          [model.field]: e.currentTarget.checked,
        },
      }),
    );
    dispatch(apiRequest.saveOBCloudSensors());
  };

  // Since cloudWatch and cloudTrail are both supported, this function changes both values
  // Remove this and set awsCloudTrail checkbox event to handleCheckboxChange when cloudWatch is removed entirely
  const handleCheckboxChangeCloudWatchTrail = (
    e: React.SyntheticEvent<HTMLInputElement>,
  ) => {
    setAWSCloudTrail(e.currentTarget.checked);
    setAWSCloudWatch(e.currentTarget.checked);
    dispatch(
      ONBOARDING_ACTIONS.EDIT_CLOUD_SENSORS({
        ...cloudSensors,
        aws: {
          ...cloudSensors['aws'],
          cloudTrail: e.currentTarget.checked,
          cloudWatch: e.currentTarget.checked,
        },
      }),
    );
    dispatch(apiRequest.saveOBCloudSensors());
  };

  const awsTitle = getAccordionHeader(
    awsCompleted,
    expandedItems,
    ACCORDION_KEYS.AWS,
    'Amazon Web Services (AWS) complete',
    'Amazon Web Services (AWS) incomplete',
  );
  const azureTitle = getAccordionHeader(
    azureCompleted,
    expandedItems,
    ACCORDION_KEYS.AZURE,
    'Microsoft Azure complete',
    'Microsoft Azure incomplete',
  );
  const o365Title = getAccordionHeader(
    o365Completed,
    expandedItems,
    ACCORDION_KEYS.O365,
    'Microsoft Office 365 complete',
    'Microsoft Office 365 incomplete',
  );
  const sfTitle = getAccordionHeader(
    sfCompleted,
    expandedItems,
    ACCORDION_KEYS.SF,
    'Salesforce complete',
    'Salesforce incomplete',
  );

  const iManageTitle = getAccordionHeader(
    iManageCompleted,
    expandedItems,
    ACCORDION_KEYS.IMANAGE,
    'iManage Threat Manager complete',
    'iManage Threat Manager incomplete',
  );

  const geoTitle = getAccordionHeader(
    geoCompleted,
    expandedItems,
    ACCORDION_KEYS.GEO,
    'Approved login countries complete',
    'Approved login countries incomplete',
  );

  return (
    <OnboardingSection
      title={OnboardingSections.CLOUD_SENSOR_CONFIG}
      completed={
        awsCompleted &&
        azureCompleted &&
        o365Completed &&
        sfCompleted &&
        geoCompleted &&
        iManageCompleted
      }
    >
      <OnboardingAccordionHeader
        description={ACCORDION_DESCRIPTION}
        isCollapsed={isCollapsed}
        expandAll={expandAll}
        collapseAll={collapseAll}
      />
      <div className="ob-accordion-section">
        <Accordion
          itemGap={20}
          expandedItems={expandedItems}
          onChange={handleOnChange}
        >
          <Accordion.Item
            className="mr-3"
            itemKey={ACCORDION_KEYS.GEO}
            header={geoTitle}
            footer={
              <SensorItemFooter
                canEdit={canEdit}
                isComplete={geoCompleted}
                sensorType="geo"
                disabled={geoCountries.length === 0 && !geoCompleted}
              />
            }
          >
            <form className="pl-2">
              <p className="ob-section-description">
                Select the approved login countries for cloud services used by
                your organization.
              </p>
              <Label className="font-weight-bold">
                Approved Login Countries
              </Label>
              <InputDropdown
                id="geoLoginCountries"
                menuPortalTarget={document.body}
                isCreatable={false}
                isMulti={true}
                showDropdownArrow={true}
                onChange={(value) =>
                  handleSelectChange(value, setGeoCountries, {
                    cloudSensor: 'geo',
                    field: 'countries',
                  })
                }
                options={COUNTRIES_SELECT_OPTIONS_USA_FIRST}
                placeholder="Choose an approved login country"
                styles={DARK_PLACEHOLDER}
                value={geoCountries}
                isDisabled={!canEdit}
                classNamePrefix="geoLoginCountries"
              />
            </form>
          </Accordion.Item>
          {purchasedCloudSensors.includes(OBCloudSensorTypes.aws) && (
            <Accordion.Item
              className="mr-3"
              itemKey={ACCORDION_KEYS.AWS}
              header={awsTitle}
              footer={
                <SensorItemFooter
                  canEdit={canEdit}
                  isComplete={awsCompleted}
                  sensorType="aws"
                  disabled={awsRegions.length === 0 && !awsCompleted}
                />
              }
            >
              <form className="pl-2">
                <p className="ob-section-description">
                  Select the Amazon Web Services (AWS) regions and services used
                  by your organization.
                </p>
                <Label className="font-weight-bold">Regions</Label>
                <InputDropdown
                  id="awsRegions"
                  menuPortalTarget={document.body}
                  isCreatable={false}
                  isMulti={true}
                  showDropdownArrow={true}
                  onChange={(value) =>
                    handleSelectChange(value, setAWSRegions, {
                      cloudSensor: 'aws',
                      field: 'regions',
                    })
                  }
                  options={REGIONS}
                  placeholder="Choose a region"
                  styles={DARK_PLACEHOLDER}
                  value={awsRegions}
                  isDisabled={!canEdit}
                  classNamePrefix="awsRegions"
                />
                <div style={{ display: 'grid', paddingTop: '10px' }}>
                  <Label className="font-weight-bold">Services</Label>
                  <p className="ob-section-description">
                    Do you have the following services enabled?
                  </p>
                  <Label className="ob-accordion-checkbox" check>
                    <Input
                      type="checkbox"
                      data-cy="awsCloudTrail"
                      checked={awsCloudTrail}
                      disabled={!canEdit}
                      onChange={(event) => {
                        handleCheckboxChangeCloudWatchTrail(event);
                        // handleCheckboxChange(event, setAWSCloudTrail, {
                        //   cloudSensor: "aws",
                        //   field: "cloudTrail",
                        // });
                      }}
                    />
                    Amazon CloudTrail
                  </Label>
                  <Label className="ob-accordion-checkbox" check>
                    <Input
                      type="checkbox"
                      data-cy="awsGuardDuty"
                      checked={awsGuardDuty}
                      disabled={!canEdit}
                      onChange={(event) =>
                        handleCheckboxChange(event, setAWSGuardDuty, {
                          cloudSensor: 'aws',
                          field: 'guardDuty',
                        })
                      }
                    />
                    Amazon GuardDuty
                  </Label>
                  <Label className="ob-accordion-checkbox" check>
                    <Input
                      type="checkbox"
                      data-cy="awsAcl"
                      checked={awsAcl}
                      disabled={!canEdit}
                      onChange={(event) =>
                        handleCheckboxChange(event, setAWSAcl, {
                          cloudSensor: 'aws',
                          field: 'acl',
                        })
                      }
                    />
                    Amazon Web Application Firewall/ACL
                  </Label>
                  <Label className="ob-accordion-checkbox" check>
                    <Input
                      type="checkbox"
                      data-cy="awsEks"
                      checked={awsEks}
                      disabled={!canEdit}
                      onChange={(event) =>
                        handleCheckboxChange(event, setAWSEks, {
                          cloudSensor: 'aws',
                          field: 'eks',
                        })
                      }
                    />
                    Elastic Kubernetes Service/EKS
                  </Label>
                </div>
              </form>
            </Accordion.Item>
          )}
          {/*TODO: wait for toolkit to provide disable accordion support*/}
          {purchasedCloudSensors.includes(OBCloudSensorTypes.box) && (
            <Accordion.Item
              className="mr-3"
              header="Box complete"
              itemKey={ACCORDION_KEYS.BOX}
              disabled
            />
          )}
          {purchasedCloudSensors.includes(OBCloudSensorTypes.gsuite) && (
            <Accordion.Item
              className="mr-3"
              header="G Suite complete"
              itemKey={ACCORDION_KEYS.GSUITE}
              disabled
            />
          )}
          {purchasedCloudSensors.includes(OBCloudSensorTypes.imanage) && (
            <Accordion.Item
              className="mr-3"
              itemKey={ACCORDION_KEYS.IMANAGE}
              header={iManageTitle}
              footer={
                <SensorItemFooter
                  canEdit={canEdit}
                  isComplete={iManageCompleted}
                  sensorType="imanage"
                  disabled={false}
                />
              }
            >
              <form className="pl-2">
                <p className="ob-section-description">
                  Please acknowledge your subscription to Threat Manager. Select
                  the iManage licenses used by your organization.
                </p>
                <Label className="font-weight-bold">Licenses</Label>
                <InputDropdown
                  menuPortalTarget={document.body}
                  id="iManageLicenses"
                  isCreatable={false}
                  isMulti={true}
                  showDropdownArrow={true}
                  onChange={(value) =>
                    handleSelectChange(value, setIManageLicenses, {
                      cloudSensor: 'imanage',
                      field: 'licenses',
                    })
                  }
                  options={IMANAGE_LICENSES}
                  placeholder="Choose a license"
                  styles={DARK_PLACEHOLDER}
                  value={iManageLicenses}
                  isDisabled={!canEdit}
                  classNamePrefix="iManageLicenses"
                />
              </form>
            </Accordion.Item>
          )}
          {purchasedCloudSensors.includes(OBCloudSensorTypes.azure) && (
            <Accordion.Item
              className="mr-3"
              itemKey={ACCORDION_KEYS.AZURE}
              header={azureTitle}
              footer={
                <SensorItemFooter
                  canEdit={canEdit}
                  isComplete={azureCompleted}
                  sensorType="azure"
                  disabled={false}
                />
              }
            >
              <form className="pl-2">
                <p className="ob-section-description">
                  Select the Microsoft Azure services used by your organization.
                </p>
                <div style={{ display: 'grid', paddingTop: '10px' }}>
                  <Label className="font-weight-bold">Services</Label>
                  <Label className="ob-accordion-checkbox" check>
                    <Input
                      type="checkbox"
                      data-cy="azureSecurityCenter"
                      checked={azureSecurityCenter}
                      disabled={!canEdit}
                      onChange={(event) =>
                        handleCheckboxChange(event, setAzureSecurityCenter, {
                          cloudSensor: 'azure',
                          field: 'securityCenter',
                        })
                      }
                    />
                    Azure Security Center
                  </Label>
                  <Label className="ob-accordion-checkbox" check>
                    <Input
                      type="checkbox"
                      data-cy="azureSentinel"
                      checked={azureSentinel}
                      disabled={!canEdit}
                      onChange={(event) =>
                        handleCheckboxChange(event, setAzureSentinel, {
                          cloudSensor: 'azure',
                          field: 'sentinel',
                        })
                      }
                    />
                    Azure Sentinel
                  </Label>
                </div>
              </form>
            </Accordion.Item>
          )}
          {purchasedCloudSensors.includes(OBCloudSensorTypes.o365) && (
            <Accordion.Item
              className="mr-3"
              itemKey={ACCORDION_KEYS.O365}
              header={o365Title}
              footer={
                <SensorItemFooter
                  canEdit={canEdit}
                  isComplete={o365Completed}
                  sensorType="o365"
                  disabled={o365Licenses.length === 0 && !o365Completed}
                />
              }
            >
              <form className="pl-2">
                <p className="ob-section-description">
                  Select the Microsoft Office 365 licenses and services used by
                  your organization.
                </p>
                <Label className="font-weight-bold">Licenses</Label>
                <InputDropdown
                  menuPortalTarget={document.body}
                  id="o365Licenses"
                  isCreatable={false}
                  isMulti={true}
                  showDropdownArrow={true}
                  onChange={(value) =>
                    handleSelectChange(value, setO365Licenses, {
                      cloudSensor: 'o365',
                      field: 'licenses',
                    })
                  }
                  options={O365_LICENSES}
                  placeholder="Choose a license"
                  styles={DARK_PLACEHOLDER}
                  value={o365Licenses}
                  isDisabled={!canEdit}
                  classNamePrefix="o365Licenses"
                />
                <div style={{ display: 'grid', paddingTop: '10px' }}>
                  <Label className="ob-accordion-checkbox" check>
                    <Input
                      type="checkbox"
                      checked={o365DLP}
                      data-cy="o365DLP"
                      disabled={!canEdit}
                      onChange={(event) =>
                        handleCheckboxChange(event, setO365DLP, {
                          cloudSensor: 'o365',
                          field: 'dlp',
                        })
                      }
                    />
                    Data Loss Prevention (DLP)
                  </Label>
                </div>
              </form>
            </Accordion.Item>
          )}
          {purchasedCloudSensors.includes(OBCloudSensorTypes.salesforce) && (
            <Accordion.Item
              className="mr-3"
              itemKey={ACCORDION_KEYS.SF}
              header={sfTitle}
              footer={
                <SensorItemFooter
                  canEdit={canEdit}
                  isComplete={sfCompleted}
                  sensorType="sf"
                  disabled={sfLicenses.length === 0 && !sfCompleted}
                />
              }
            >
              <form className="pl-2">
                <p className="ob-section-description">
                  Select the Salesforce licenses used by your organization.
                </p>
                <Label className="font-weight-bold">Licenses</Label>
                <InputDropdown
                  menuPortalTarget={document.body}
                  id="sfLicenses"
                  isCreatable={false}
                  isMulti={true}
                  showDropdownArrow={true}
                  onChange={(value) =>
                    handleSelectChange(value, setSFLicenses, {
                      cloudSensor: 'sf',
                      field: 'licenses',
                    })
                  }
                  options={SF_LICENSES}
                  placeholder="Choose a license"
                  styles={DARK_PLACEHOLDER}
                  value={sfLicenses}
                  isDisabled={!canEdit}
                  classNamePrefix="sfLicenses"
                />
              </form>
            </Accordion.Item>
          )}
          {purchasedCloudSensors.includes(OBCloudSensorTypes.gcp) && (
            <Accordion.Item
              className="mr-3"
              itemKey={ACCORDION_KEYS.GCP}
              header={'Google Cloud Platform'}
            >
              <form className="pl-2">
                <p className="ob-section-description">
                  Select the license(s) currently enabled in your environment:
                </p>
                <Label className="font-weight-bold">Licenses</Label>
                <InputDropdown
                  menuPortalTarget={document.body}
                  id="gcpLicenses"
                  isCreatable={false}
                  isMulti={true}
                  showDropdownArrow={true}
                  onChange={(value) =>
                    handleSelectChange(value, setGCPLicenses, {
                      cloudSensor: 'gcp',
                      field: 'licenses',
                    })
                  }
                  options={GCP_LICENSES}
                  placeholder="Choose a license"
                  styles={DARK_PLACEHOLDER}
                  value={gcpLicenses}
                  isDisabled={!canEdit}
                  classNamePrefix="gcpLicenses"
                />
              </form>
            </Accordion.Item>
          )}
        </Accordion>
      </div>
    </OnboardingSection>
  );
};

export default OnboardingCloudSensorConfig;
